import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';
import type { ItemCondition, ItemStatus } from './status';

// NOTE: while testing, all /v1/dispatch calls are rerouted to the local
// logistic service by the TODO interceptor in `@/utils/axios` — see there.

// ─────────────────────────────────────────────────────────────────────────────
// Status enums — mirror the BE CHECK constraints.
// ─────────────────────────────────────────────────────────────────────────────

export type DispatchStatus =
  | 'PREVIEW'
  | 'COMMITTED'
  | 'PARTIAL'
  | 'FAILED'
  | 'CANCELLED';

export type BatchStatus =
  | 'PLANNED'
  | 'DEFERRED'
  | 'DISPATCHED'
  | 'FAILED';

/**
 * Planner-input mode (pre-dispatch TRD §D1).
 *   - `STANDARD` — plans HUB_SCANNED items only (today's behaviour).
 *   - `PRE_DISPATCH` — also plans CREATED (not-yet-scanned) items at the
 *     hub, so ops can tune drivers before the goods physically arrive.
 */
export type DispatchMode = 'STANDARD' | 'PRE_DISPATCH';

/**
 * Live item status surfaced per dispatch item (§D9). Pre-commit an item is only
 * ever `CREATED` (belum di-scan) or `HUB_SCANNED`. `LOST` / `CANCELLED` items
 * are excluded from stop/batch itemCount + totalWeightKg by the BE, but still
 * returned in `items[]` so the UI can show them.
 *
 * v2 kept `DISPATCHED`: a successful commit still moves items `HUB_SCANNED →
 * DISPATCHED` (the rider app reads that value), and the parcel is physically
 * still at the hub until handover. From there the item walks
 * `HANDOVER_SCANNED → IN_DELIVERY → COMPLETED`, or into the retur branch, so the
 * full read vocabulary applies here. Damage is `condition`, not a status.
 */
export type DispatchItemStatus = ItemStatus;

// ─────────────────────────────────────────────────────────────────────────────
// List / pagination — shape mirrors shipments `ListPagination`.
// ─────────────────────────────────────────────────────────────────────────────

export interface ListPagination {
  total: number;
  size: number;
  page: number;
  lastPage: number;
}

/** Hub/pitstop snapshot frozen at preview — null on rows made before it existed. */
export interface DispatchHubSnapshot {
  id: number;
  name: string;
  /** Pitstop code (e.g. DASH_KMG) — absent on snapshots frozen before it shipped. */
  code?: string | null;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface Dispatch {
  id: string;
  code: string;
  hubId: string;
  /** Frozen at preview; render `hub.name`, fall back to `hubId` on old rows. */
  hub: DispatchHubSnapshot | null;
  /** Distinct clients planned in — comma-join for the list; null on old rows. */
  clients: ShipmentClientSnapshot[] | null;
  status: DispatchStatus;
  totalItems: number;
  totalBatches: number;
  deferredBatchCount: number;
  deferredItemCount: number;
  unbatchableCount: number;
  riderCount: number;
  triggeredBy: string;
  committedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DispatchListParams {
  hubId?: string;
  /** Only dispatches whose plan contains ANY of these clients (multi-select). */
  clientIds?: number[];
  status?: DispatchStatus;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

export interface DispatchListResponse {
  data: Dispatch[];
  pagination: ListPagination;
}

// ─────────────────────────────────────────────────────────────────────────────
// Persisted-preview model: every dispatch starts as PREVIEW, can be edited
// (PATCH), then committed (POST /:id/commit). The detail shape carries the
// full batches/stops/items tree so the review screen can render without
// extra fetches.
// ─────────────────────────────────────────────────────────────────────────────

export interface DispatchRiderSnapshot {
  id: number;
  code: string;
  name: string;
}

/** Client scope chip persisted on the dispatch at preview time (§D13). */
export interface ShipmentClientSnapshot {
  id: number;
  name: string;
}

/** Row of `GET /v1/dispatch/collectable-clients` — the ops client picker. */
export interface CollectableClient {
  id: number;
  name: string;
  itemCount: number;
}

export interface DispatchDetailItem {
  id: string;
  itemId: string;
  itemName: string;
  waybill: string;
  /**
   * Client booking reference of the parent shipment — shown next to the waybill
   * (`waybill (bookingId)`) so ops can match a parcel against the client's
   * order. Optional: older rows / payloads omit it.
   */
  bookingId?: string | null;
  /** The item's shipment; absent on an older BE. */
  shipmentId?: string | null;
  /** Hub → destination in metres (`shipments.distance`); null until Dispatch
   *  measures it. Absent on an older BE. */
  hubDistanceM?: number | null;
  weightKg: number;
  volumeM3: number;
  /** Live item status (§D9). `CREATED` = belum di-scan di hub — unless `skipsHubScan`. */
  itemStatus: DispatchItemStatus;
  /**
   * The goods never pass a hub scan before the run — the rider collects them
   * (scheduled pitstop pickup, Scheduled Instant, or a retrieval "Titik
   * Dropoff" upload). `CREATED` is then their normal ready state, not a
   * warning. Absent on responses from a BE that predates it.
   */
  skipsHubScan?: boolean;
  /** Damage flag (v2 `condition`), independent of `itemStatus`. */
  condition?: ItemCondition | null;
}

/**
 * What the rider does at a stop. `DROPOFF` = hand goods over at the shipment
 * destination. `PICKUP` = collect goods at the shipment ORIGIN (a retrieval
 * upload — "Titik Dropoff" import — whose goods ride back to the hub). One
 * dispatch round can hold both kinds.
 */
export type StopKind = 'PICKUP' | 'DROPOFF';

/**
 * Which way a trip runs relative to the hub/pitstop, read off its stops:
 * `DROPOFF` (departs loaded, every stop a dropoff), `PICKUP` (ends there
 * loaded, every stop a pickup), or `MIXED` — which the planner never
 * produces and ops can no longer create by merging/moving; only legacy data
 * or a deferred re-plan gone wrong can show it. `null` for a batch with no
 * stops. A stop without `kind` (older BE) counts as a dropoff.
 */
export type TripKind = StopKind | 'MIXED';

export const tripKindOf = (
  stops: ReadonlyArray<{ kind?: StopKind }>
): TripKind | null => {
  if (stops.length === 0) return null;
  const pickups = stops.filter((s) => s.kind === 'PICKUP').length;
  if (pickups === 0) return 'DROPOFF';
  if (pickups === stops.length) return 'PICKUP';
  return 'MIXED';
};

/**
 * May a stop of `stopKind` join a trip whose stops are `targetStops`? Only
 * when the trip is empty or already runs the same way — a pickup never rides
 * on a drop run (and the reverse), because the two start/end at opposite
 * ends of the hub/pitstop.
 */
export const stopFitsTrip = (
  stopKind: StopKind | undefined,
  targetStops: ReadonlyArray<{ kind?: StopKind }>
): boolean => {
  const target = tripKindOf(targetStops);
  return target === null || target === (stopKind ?? 'DROPOFF');
};

export interface DispatchDetailStop {
  id: string;
  sequence: number;
  destinationKey: string;
  /** Where the rider goes — the destination, or the ORIGIN on a PICKUP stop. */
  destinationAddress: string;
  /** Absent on responses from a BE that predates it — treat as `DROPOFF`. */
  kind?: StopKind;
  /** Contact name of the stop's first item: receiver on a DROPOFF, sender on a PICKUP. `''` if no items. */
  receiverName: string;
  /** Contact phone from the same first item. `null` when not captured. */
  destinationPhone: string | null;
  destinationLat: number;
  destinationLong: number;
  zoneCode: string | null;
  zoneName: string | null;
  itemCount: number;
  totalWeightKg: number;
  totalVolumeM3: number;
  distinctClientCount: number;
  /** The stop's clients by name — distinct, in item order. Absent/empty on
   *  responses from a BE without the field or stops whose shipments are gone. */
  clients?: ShipmentClientSnapshot[];
  computedStopTimeMin: number;
  /** Hub → this stop in metres (`shipments.distance`, measured by Dispatch
   *  when empty). Null until measured; absent on an older BE. */
  hubDistanceM?: number | null;
  /** Mapbox leg ENDING at this stop. Null pre-snapshot or on call failure. */
  legDistanceM: number | null;
  legDurationSec: number | null;
  /** Items on this stop still `CREATED` — belum di-scan (§D9). Total, incl. deferred. */
  unscannedItemCount: number;
  /** `CREATED` items that are NOT deferred — the ones ops must scan to ship. */
  activeUnscannedItemCount: number;
  /** `CREATED` items that are deferred (item-level or in a DEFERRED batch). */
  deferredUnscannedItemCount: number;
  /** Stop-level scan warning flag — `unscannedItemCount > 0` (§D9). */
  hasUnscannedItems: boolean;
  items: DispatchDetailItem[];
}

export interface DispatchDetailBatch {
  id: string;
  code: string;
  assignedRiderCode: string | null;
  assignedRiderId: number | null;
  assignedRiderName: string | null;
  tripSequence: number | null;
  stopCount: number;
  itemCount: number;
  totalWeightKg: number;
  totalVolumeM3: number;
  estimatedTimeMinutes: number;
  status: BatchStatus;
  /**
   * Why the planner deferred this batch (e.g. `NO_RIDER_CAPACITY` — no
   * rider had enough weight/volume/shift budget left). Null for
   * non-DEFERRED batches.
   */
  deferReason: string | null;
  /**
   * Ops force-assigned this batch past the run's weight/volume caps — the
   * manual rescue for OVERSIZED_FOR_CAP items. Render a warning badge and
   * show the over-cap load in the danger tone.
   */
  forced: boolean;
  /**
   * Encoded polyline (precision 5) of the full hub→stops Mapbox route.
   * Decode with `google.maps.geometry.encoding.decodePath`. Null = not
   * yet snapshotted — DispatchMap falls back to straight-line legs.
   */
  routePolyline: string | null;
  routeDistanceM: number | null;
  routeDurationSec: number | null;
  routeComputedAt: string | null;
  bridgedAt: string | null;
  /** Roll-up of the stops' unscanned (`CREATED`) items (§D9). Total, incl. deferred. */
  unscannedItemCount: number;
  /** Non-deferred unscanned items — 0 for a whole DEFERRED batch. */
  activeUnscannedItemCount: number;
  /** Deferred unscanned items (item-level or the whole DEFERRED batch). */
  deferredUnscannedItemCount: number;
  /** Batch-level scan warning flag — `unscannedItemCount > 0` (§D9). */
  hasUnscannedItems: boolean;
  stops: DispatchDetailStop[];
}

export interface DispatchDetail {
  id: string;
  code: string;
  hubId: string;
  /** Hub/pitstop snapshot frozen at preview ({id, name, code, …}); null on rows made before it existed. */
  hub?: DispatchHubSnapshot | null;
  /** Planner-input mode frozen at preview (§D1). */
  mode: DispatchMode;
  /** Client scope frozen at preview (§D13). `null` = all clients. */
  clients: ShipmentClientSnapshot[] | null;
  /** Dispatch-level total of `CREATED` items — incl. deferred (§D9). Always 0 for STANDARD. */
  unscannedItemCount: number;
  /** Non-deferred unscanned items — the count ops must clear to ship. Use for the banner. */
  activeUnscannedItemCount: number;
  /** Deferred unscanned items (item-level or in DEFERRED batches). */
  deferredUnscannedItemCount: number;
  /** Other PREVIEW dispatches at the hub auto-cancelled by this commit. */
  supersededDispatchCodes?: string[];
  ridersSnapshot: DispatchRiderSnapshot[];
  maxWeightKg: number;
  maxVolumeM3: number;
  maxStopRadiusKm: number;
  shiftMinutes: number;
  timeModelSnapshot: Record<string, number>;
  clientBuffersSnapshot: Record<string, number>;
  totalItems: number;
  totalBatches: number;
  deferredBatchCount: number;
  deferredItemCount: number;
  unbatchableCount: number;
  status: DispatchStatus;
  plannerStats: Record<string, unknown> | null;
  triggeredBy: string;
  committedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * Bridge failure, when a commit could not hand every PLANNED batch to the
   * delivery service: `error` is the short code (`BRIDGE_FAILED`),
   * `errorDetail` the stage + one "batch <id> (provider <n>): <message>" line
   * per failed call, verbatim from DS. Both null on success and on PREVIEW.
   */
  error?: string | null;
  errorDetail?: string | null;
  batches: DispatchDetailBatch[];
}

/**
 * An item the planner refused as OVERSIZED_FOR_CAP — its single weight or
 * volume exceeds the dispatch's frozen caps, so no planner run can ever place
 * it. Listed live by `GET /v1/dispatch/:id/unbatchable`; rescued via
 * `dispatchesAPI.forceAssign`.
 */
export interface UnbatchableItem {
  itemId: string;
  shipmentId: string;
  itemName: string;
  waybill: string;
  bookingId: string;
  receiverName: string;
  clientId: number;
  clientName: string;
  itemStatus: string;
  weightKg: number;
  volumeM3: number;
  destinationAddress: string;
  overWeight: boolean;
  overVolume: boolean;
}

export interface UnbatchableItemsResponse {
  maxWeightKg: number;
  maxVolumeM3: number;
  items: UnbatchableItem[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Request payloads
// ─────────────────────────────────────────────────────────────────────────────

export interface PreviewDispatchPayload {
  hubId: string;
  riders: DispatchRiderSnapshot[];
  /**
   * Planner-input mode (§D1). Omit for `STANDARD` (today's behaviour).
   * `PRE_DISPATCH` also plans `CREATED` items at the hub.
   */
  mode?: DispatchMode;
  /** Client scope (§D13). Omit or `[]` = ALL clients. */
  clientIds?: number[];
  /**
   * Optional per-run caps overrides — any omitted field falls back to the
   * hub's DISPATCH_CONFIG default (see `dispatchesAPI.config`). Soft caps are
   * the planner's "prefer under this" target; hard caps are the ceiling.
   */
  maxWeightKg?: number;
  softMaxWeightKg?: number;
  maxVolumeM3?: number;
  softMaxVolumeM3?: number;
  maxStopRadiusKm?: number;
  shiftMinutes?: number;
  softShiftMinutes?: number;
  maxStopsPerBatch?: number;
  softMaxStopsPerBatch?: number;
}

/**
 * Effective planner defaults from `GET /v1/dispatch/config` (env-driven,
 * read per-request so env re-tunes show up without a deploy). Used to prefill
 * the caps inputs on the create-dispatch form.
 */
export interface DispatchConfig {
  maxWeightKg: number;
  softMaxWeightKg: number;
  maxVolumeM3: number;
  softMaxVolumeM3: number;
  shiftMinutes: number;
  softShiftMinutes: number;
  maxStopsPerBatch: number;
  softMaxStopsPerBatch: number;
}

/**
 * Item disposition (item-disposition-fe-integration.md). One endpoint per
 * action — the action is the path, no `markAs`/`mode` field. `CANCEL` / `LOST`
 * are terminal; `DEFER` is reversible (item stays, flagged deferred).
 */
export type DispatchDisposition = 'CANCEL' | 'LOST' | 'DEFER';

/** `POST /:id/riders` (§D5) — full snapshots; no server-side rider lookup. */
export interface AddDispatchRidersPayload {
  riders: DispatchRiderSnapshot[];
}

/**
 * Refresh strategy (§D12).
 *   - `APPEND_NEW` (default) — plan only hub arrivals not yet in the
 *     dispatch into NEW batches; existing batches + ops tuning preserved.
 *   - `REPLACE_ALL` — re-plan every in-scope item from scratch; tuning lost.
 */
export type RefreshDispatchStrategy = 'APPEND_NEW' | 'REPLACE_ALL';

export interface RefreshDispatchPayload {
  strategy?: RefreshDispatchStrategy;
}

export interface RefreshSummary {
  strategy: RefreshDispatchStrategy;
  addedItems: number;
  addedBatches: number;
}

export type RefreshDispatchResult = DispatchDetail & {
  refreshSummary?: RefreshSummary;
};

/** Row of the commit 409 `UNSCANNED_ITEMS` body (§D10). */
export interface UnscannedItem {
  itemId: string;
  itemName: string;
  invoice: string;
  shipmentId: string;
  bookingId: string;
}

/**
 * Commit rejected because a shipment in the dispatch still has `CREATED`
 * (belum di-scan) items — shipment-scoped gate (§D10). Carries the item
 * list so the UI can tell ops exactly which barang to scan / mark hilang.
 */
export class UnscannedItemsError extends Error {
  readonly code = 'UNSCANNED_ITEMS';
  readonly unscannedItemCount: number;
  readonly unscannedItems: UnscannedItem[];

  constructor(
    message: string,
    unscannedItemCount: number,
    unscannedItems: UnscannedItem[]
  ) {
    super(message);
    this.name = 'UnscannedItemsError';
    this.unscannedItemCount = unscannedItemCount;
    this.unscannedItems = unscannedItems;
  }
}

/**
 * Global-export failure (`GET /v1/dispatch/export`). Known codes:
 *   400 `EXPORT_RANGE_TOO_WIDE` — from/to span more than 62 days
 *   400 `EXPORT_UNKNOWN_COLUMN` — an unrecognized `columns` key
 */
export class DispatchExportError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'DispatchExportError';
    this.code = code;
  }
}

export interface UpdateDispatchPayload {
  /**
   * Move stops to a different rider — or to a specific batch/trip.
   *
   * This is also the **restore-from-deferred** path: a stop sitting in the
   * dispatch's rider-less DEFERRED batch is restored by reassigning it onto a
   * rider (see `defer` / the "Ditahan" bucket). Item-level defer no longer
   * exists — defer is stop-scoped only.
   *
   * - `toBatchId` omitted: stop lands on the target rider's smallest-load
   *   PLANNED batch (or a fresh batch if the rider has none yet).
   * - `toBatchId` set: stop moves into that exact PLANNED batch (must belong
   *   to this dispatch); `rider` is ignored by the BE but still required to be
   *   a valid `ridersSnapshot` entry — pass the target batch's rider. Use this
   *   to move a stop between trips of the same rider, or to a specific trip.
   *
   * Either way the target batch is re-routed (NN re-sequence + Mapbox
   * re-snapshot) and an emptied source batch is deleted. No capacity guard —
   * the target can go over 100%; warn in the UI if that matters.
   */
  stopReassignments?: Array<{
    stopId: string;
    rider: DispatchRiderSnapshot;
    /** Target PLANNED batch id — move to this exact trip. */
    toBatchId?: string;
  }>;
}

/**
 * Rider-level batch reassignment mode for `POST /:id/reassign-rider`.
 *   - `TRANSFER` — move ALL of `fromRider`'s PLANNED batches to `toRider`.
 *   - `SWAP` — exchange the two riders' batch sets in place.
 */
export type RiderReassignMode = 'TRANSFER' | 'SWAP';

export interface ReassignRiderPayload {
  /** Rider whose batches move. Must own ≥1 PLANNED batch in the dispatch. */
  fromRiderId: number;
  /** Target rider. Must be selected on the dispatch and differ from source. */
  toRiderId: number;
  mode: RiderReassignMode;
}

/**
 * Single-batch reassignment mode for
 * `POST /:id/batches/:batchId/reassign-rider`. Same two modes as the
 * rider-level move, but the from-side is narrowed to one batch.
 *   - `TRANSFER` — move this batch to `toRider`. If `toRider` already has a
 *     PLANNED batch, the moved stops+items MERGE into their batch awal and
 *     the trip is re-routed (§D8/§D11); otherwise the batch moves intact.
 *   - `SWAP` — move this batch to `toRider` AND move every PLANNED batch
 *     `toRider` currently owns back to this batch's current owner.
 */
export type BatchReassignMode = RiderReassignMode;

export interface ReassignBatchPayload {
  /**
   * Target rider. Must be in the dispatch's `ridersSnapshot` and differ from
   * the batch's current owner (the source rider is implicit — the server
   * reads it from the batch). 400 if it's the batch's current owner.
   */
  toRiderId: number;
  mode: BatchReassignMode;
}

type Envelope<T> = { status: string | number; message?: string; data: T };

const unwrap = <T>(payload: Envelope<T> | T): T =>
  payload && typeof payload === 'object' && 'data' in (payload as any)
    ? (payload as Envelope<T>).data
    : (payload as T);

export const dispatchesAPI = {
  /** Effective planner caps/defaults (env-driven). Prefills the create form. */
  config: async (): Promise<DispatchConfig> => {
    try {
      const response = await Axios.get('/v1/dispatch/config');
      return unwrap<DispatchConfig>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  list: async (
    params: DispatchListParams = {}
  ): Promise<DispatchListResponse> => {
    try {
      // clientIds rides as one comma-separated param (BE splits it) — axios
      // would otherwise serialize arrays as repeated keys.
      const { clientIds, ...rest } = params;
      const response = await Axios.get('/v1/dispatch', {
        params: {
          ...rest,
          ...(clientIds && clientIds.length > 0
            ? { clientIds: clientIds.join(',') }
            : {}),
        },
      });
      return unwrap<DispatchListResponse>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Clients that actually have collectable items at a hub (§D13) — feeds
   * the client picker on the preview form. Pass `mode: 'PRE_DISPATCH'` so
   * counts include not-yet-scanned (`CREATED`) items, matching what a
   * pre-dispatch preview would pull.
   */
  collectableClients: async (
    hubId: string,
    mode?: DispatchMode
  ): Promise<CollectableClient[]> => {
    try {
      const response = await Axios.get('/v1/dispatch/collectable-clients', {
        params: { hubId, ...(mode ? { mode } : {}) },
      });
      const data = unwrap<CollectableClient[]>(response.data);
      // Defensive: a BE whose route table matches `/:id` first (or an older
      // envelope) can 200 with an object here — never let a non-array
      // reach the picker's `.map`.
      if (!Array.isArray(data)) {
        console.warn(
          '[dispatchesAPI.collectableClients] expected array, got:',
          response.data
        );
        return [];
      }
      return data;
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Create a PREVIEW dispatch. Returns the full detail (batches +
   * stops + items). Items are not yet assigned to riders.
   */
  preview: async (
    payload: PreviewDispatchPayload
  ): Promise<DispatchDetail> => {
    try {
      const response = await Axios.post(
        '/v1/dispatch/preview',
        payload
      );
      return unwrap<DispatchDetail>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /** Fetch a dispatch + nested batches/stops/items by id. */
  get: async (id: string): Promise<DispatchDetail> => {
    try {
      const response = await Axios.get(`/v1/dispatch/${id}`);
      return unwrap<DispatchDetail>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Edit a PREVIEW dispatch — stop reassignments (incl. restoring a deferred
   * stop by assigning it onto a rider). Returns the refreshed detail. 409 if
   * dispatch is not in PREVIEW status.
   */
  update: async (
    id: string,
    payload: UpdateDispatchPayload
  ): Promise<DispatchDetail> => {
    try {
      const response = await Axios.patch(
        `/v1/dispatch/${id}`,
        payload
      );
      return unwrap<DispatchDetail>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Manually re-sequence one batch's stops on a PREVIEW dispatch
   * (drag-to-reorder). `stopIds` must be the batch's COMPLETE stop set in
   * the new visit order — no omissions, extras, or duplicates. The BE
   * rewrites `sequence` 1..N and re-snapshots the batch's Mapbox route +
   * per-stop legs. Returns the refreshed detail. 409 if not PREVIEW.
   */
  reorderStops: async (
    id: string,
    batchId: string,
    stopIds: string[]
  ): Promise<DispatchDetail> => {
    try {
      const response = await Axios.put(
        `/v1/dispatch/${id}/batches/${batchId}/stop-order`,
        { stopIds }
      );
      return unwrap<DispatchDetail>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Manually re-sequence one rider's trips (batches) on a PREVIEW dispatch
   * (drag-to-reorder of the batch cards). `batchIds` must be the rider's
   * COMPLETE PLANNED-batch set in the new trip order. Only `tripSequence`
   * changes — no stop moves, routes/totals untouched. Returns the refreshed
   * detail. 409 if not PREVIEW.
   */
  reorderTrips: async (
    id: string,
    riderId: number,
    batchIds: string[]
  ): Promise<DispatchDetail> => {
    try {
      const response = await Axios.put(
        `/v1/dispatch/${id}/riders/${riderId}/trip-order`,
        { batchIds }
      );
      return unwrap<DispatchDetail>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Reassign every batch a rider owns on a PREVIEW dispatch — `TRANSFER`
   * (move all of A's batches to B) or `SWAP` (exchange A's and B's batch
   * sets). Batches move intact (stops/items/totals/routes unchanged).
   * Returns the refreshed detail. 409 if dispatch is not in PREVIEW status.
   */
  reassignRider: async (
    id: string,
    payload: ReassignRiderPayload
  ): Promise<DispatchDetail> => {
    try {
      const response = await Axios.post(
        `/v1/dispatch/${id}/reassign-rider`,
        payload
      );
      return unwrap<DispatchDetail>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Reassign a single PLANNED batch on a PREVIEW dispatch to another rider —
   * `TRANSFER` (move just this batch) or `SWAP` (move this batch over and
   * pull the target's batches back to this batch's current owner). The source
   * rider is implicit (the batch's current owner); only `toRiderId` is sent.
   *
   * `TRANSFER` merges (§D8/§D11): when the target rider already has a batch,
   * the moved batch's stops+items merge into the target's batch awal (lowest
   * trip_sequence), the source batch disappears, and the target batch is
   * re-routed (NN re-sequence + Mapbox re-snapshot) — so re-render from the
   * returned detail. Target with no batch → intact move. `SWAP` is unchanged.
   * Returns the refreshed detail. 409 if the dispatch is not PREVIEW or the
   * batch is DEFERRED.
   */
  reassignBatch: async (
    id: string,
    batchId: string,
    payload: ReassignBatchPayload
  ): Promise<DispatchDetail> => {
    try {
      const response = await Axios.post(
        `/v1/dispatch/${id}/batches/${batchId}/reassign-rider`,
        payload
      );
      return unwrap<DispatchDetail>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Stop-scoped disposition — acts on ALL items at the stop. `cancel`/`lost`
   * are terminal (stop dropped); `defer` MOVES the whole stop (with its items)
   * out of the rider's batch into the dispatch's single rider-less DEFERRED
   * holding batch — it drops off the route and won't ship this commit.
   * Reversible: restore by reassigning the stop onto a rider (PATCH
   * `stopReassignments`). Returns refreshed detail.
   */
  disposeStop: async (
    id: string,
    stopId: string,
    disposition: DispatchDisposition,
    reason?: string
  ): Promise<DispatchDetail> => {
    try {
      const path = disposition.toLowerCase(); // cancel | lost | defer
      const response = await Axios.post(
        `/v1/dispatch/${id}/stops/${stopId}/${path}`,
        reason ? { reason } : {}
      );
      return unwrap<DispatchDetail>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Item-scoped disposition — terminal only (`cancel` / `lost`). `itemIds` are
   * the underlying `items.id` (the `itemId` field of a detail item, NOT `id`).
   * Emptied stops/batches are dropped. Returns refreshed detail. (There is no
   * item-level defer — defer is stop-scoped; use `disposeStop(..., 'DEFER')`.)
   */
  disposeItems: async (
    id: string,
    disposition: 'CANCEL' | 'LOST',
    itemIds: string[],
    reason?: string
  ): Promise<DispatchDetail> => {
    try {
      const path = disposition.toLowerCase(); // cancel | lost
      const response = await Axios.post(`/v1/dispatch/${id}/items/${path}`, {
        itemIds,
        ...(reason ? { reason } : {}),
      });
      return unwrap<DispatchDetail>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Add rider(s) to a PREVIEW dispatch's frozen snapshot (§D5). No re-plan —
   * the new rider starts empty; move work to them via the reassign endpoints.
   * 400 RIDER_ALREADY_IN_DISPATCH if all are already present. Returns the
   * refreshed detail (widened `ridersSnapshot`).
   */
  addRiders: async (
    id: string,
    payload: AddDispatchRidersPayload
  ): Promise<DispatchDetail> => {
    try {
      const response = await Axios.post(
        `/v1/dispatch/${id}/riders`,
        payload
      );
      return unwrap<DispatchDetail>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Items the planner refused as OVERSIZED_FOR_CAP, computed live against the
   * dispatch's frozen caps (`unbatchableCount` persists only the number).
   */
  unbatchable: async (id: string): Promise<UnbatchableItemsResponse> => {
    try {
      const response = await Axios.get(`/v1/dispatch/${id}/unbatchable`);
      return unwrap<UnbatchableItemsResponse>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Force-assign oversized items to a rider (PREVIEW only): a NEW
   * `forced = true` batch is created for the rider, capacity caps
   * deliberately bypassed. The rider auto-joins `ridersSnapshot`.
   */
  forceAssign: async (
    id: string,
    payload: { itemIds: string[]; rider: DispatchRiderSnapshot }
  ): Promise<DispatchDetail> => {
    try {
      const response = await Axios.post(
        `/v1/dispatch/${id}/force-assign`,
        payload
      );
      return unwrap<DispatchDetail>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Pull items that arrived at the hub AFTER the preview was created (§D12).
   * Re-collects with the dispatch's frozen `mode` + client scope. Known BE
   * limitation: two concurrent `REPLACE_ALL` refreshes can lost-update — the
   * caller must disable the trigger while a request is in flight.
   */
  refresh: async (
    id: string,
    payload: RefreshDispatchPayload = {}
  ): Promise<RefreshDispatchResult> => {
    try {
      const response = await Axios.post(
        `/v1/dispatch/${id}/refresh`,
        payload
      );
      return unwrap<RefreshDispatchResult>(response.data);
    } catch (error) {
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * Commit a PREVIEW dispatch → COMMITTED. Empty body. Idempotent: a
   * dispatch that's already COMMITTED / PARTIAL / FAILED returns
   * unchanged. 409 STALE_ITEMS if any item in a PLANNED batch has
   * been claimed by another commit since the preview was built.
   *
   * Throws `UnscannedItemsError` on the shipment-complete gate (§D10) —
   * 409 UNSCANNED_ITEMS when a shipment in the dispatch still has a
   * `CREATED` item. Callers should list the items so ops can scan them
   * or mark them hilang, then re-commit.
   */
  commit: async (id: string): Promise<DispatchDetail> => {
    try {
      const response = await Axios.post(
        `/v1/dispatch/${id}/commit`,
        {}
      );
      return unwrap<DispatchDetail>(response.data);
    } catch (error) {
      const data = (error as any)?.response?.data;
      // `code` first: the service's global error envelope puts the human
      // message in `error`, so the domain code only arrives when the response
      // carries it separately. Reading both keeps this working either way.
      if ((data?.code ?? data?.error) === 'UNSCANNED_ITEMS') {
        throw new UnscannedItemsError(
          data.message ?? 'Ada item yang belum di-scan di hub.',
          data.unscannedItemCount ?? data.unscannedItems?.length ?? 0,
          data.unscannedItems ?? []
        );
      }
      throw new Error(getBackendErrorMessage(error));
    }
  },

  /**
   * GLOBAL CSV export (`GET /v1/dispatch/export`, scheduled-dispatch TRD v2
   * §D7) — one row per item across every dispatch matching the filter.
   * `from`/`to` are required `YYYY-MM-DD` on committed_at, max 62 days apart
   * (400 `EXPORT_RANGE_TOO_WIDE`). `status` is a CSV of dispatch statuses,
   * `columns` a CSV of optional column keys — the Identitas group
   * (dispatch_code, dispatch_date, waybill, receiver_name,
   * destination_address) is always included server-side.
   */
  exportAll: async (params: {
    from: string;
    to: string;
    status?: DispatchStatus[] | null;
    hubId?: number | string | null;
    columns?: string[] | null;
  }): Promise<void> => {
    try {
      const response = await Axios.get('/v1/dispatch/export', {
        responseType: 'blob',
        params: {
          from: params.from,
          to: params.to,
          ...(params.status?.length
            ? { status: params.status.join(',') }
            : {}),
          ...(params.hubId != null && params.hubId !== ''
            ? { hubId: params.hubId }
            : {}),
          ...(params.columns?.length
            ? { columns: params.columns.join(',') }
            : {}),
        },
      });

      // Prefer the filename the backend set via Content-Disposition.
      const disposition = response.headers['content-disposition'] as
        | string
        | undefined;
      const match = disposition?.match(/filename="?([^"]+)"?/i);
      const filename =
        match?.[1] || `dispatches-${params.from}-${params.to}.csv`;

      const blob = new Blob([response.data], {
        type: 'text/csv;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      // Export errors come back as a Blob (responseType above) — decode the
      // JSON body so callers can branch on the code (EXPORT_RANGE_TOO_WIDE).
      const data = (error as any)?.response?.data;
      let message = '';
      let code = '';
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          message = parsed.message || '';
          code = typeof parsed.error === 'string' ? parsed.error : '';
        } catch {
          // not JSON — fall through to the generic message
        }
      }
      throw new DispatchExportError(
        code || 'UNKNOWN',
        message || getBackendErrorMessage(error)
      );
    }
  },

  /**
   * Download one dispatch as CSV (`GET /v1/dispatch/:id/export`, TRD §4.9).
   * One row per item — batch/rider/stop/shipment columns + `Delivery UID`
   * (empty until the DS bridge for that batch succeeds). The BE streams the
   * file; the client only triggers the browser download.
   */
  export: async (id: string, code?: string): Promise<void> => {
    try {
      const response = await Axios.get(`/v1/dispatch/${id}/export`, {
        responseType: 'blob',
      });

      // Prefer the filename the backend set via Content-Disposition.
      const disposition = response.headers['content-disposition'] as
        | string
        | undefined;
      const match = disposition?.match(/filename="?([^"]+)"?/i);
      const filename =
        match?.[1] || `dispatch-${code || id}.csv`;

      const blob = new Blob([response.data], {
        type: 'text/csv;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      // Export errors come back as a Blob (responseType above) — decode the
      // JSON body so the toast shows the real message.
      const data = (error as any)?.response?.data;
      let message = '';
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          message = parsed.message || parsed.error || '';
        } catch {
          // not JSON — fall through to the generic message
        }
      }
      throw new Error(message || getBackendErrorMessage(error));
    }
  },
};

export const DISPATCH_STATUS_LABELS: Record<DispatchStatus, string> = {
  PREVIEW: 'Preview',
  COMMITTED: 'Committed',
  PARTIAL: 'Partial',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
};

export const BATCH_STATUS_LABELS: Record<BatchStatus, string> = {
  PLANNED: 'Planned',
  DEFERRED: 'Deferred',
  DISPATCHED: 'Dispatched',
  FAILED: 'Failed',
};
